import { createContext, useContext, useEffect, useState } from 'react'
import i18n from './i18n'
import type { ReactNode } from 'react'

export type Language = 'en' | 'fa'

interface LanguageContextType {
  language: Language
  setLanguage: (language: Language) => void
  isRTL: boolean
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined,
)

export function LanguageProvider({ children }: { children: ReactNode }) {
  // Always initialize with 'en' to prevent SSR hydration mismatch
  // Will be updated after mount via useEffect
  const [language, setLanguageState] = useState<Language>('en')
  const [isMounted, setIsMounted] = useState(false)

  // Initialize language from localStorage or browser after mount
  useEffect(() => {
    const stored = localStorage.getItem('language')
    if (stored === 'en' || stored === 'fa') {
      setLanguageState(stored as Language)
    } else {
      // Detect browser language
      const browserLang = navigator.language.split('-')[0]
      if (browserLang === 'fa') {
        setLanguageState('fa')
      }
    }
    setIsMounted(true)
  }, [])

  const isRTL = language === 'fa'

  useEffect(() => {
    // Update i18next language
    i18n.changeLanguage(language).catch(console.error)

    // Only write to localStorage after initial mount to prevent overwriting stored value
    if (isMounted) {
      localStorage.setItem('language', language)
    }

    // Update HTML lang and dir attributes
    const root = window.document.documentElement
    root.setAttribute('lang', language)
    root.setAttribute('dir', isRTL ? 'rtl' : 'ltr')

    // Conditionally load Vazir font for Persian
    if (language === 'fa') {
      import('@fontsource/vazir')
    }
  }, [language, isRTL])

  const setLanguage = (newLanguage: Language) => {
    setLanguageState(newLanguage)
  }

  return (
    <LanguageContext.Provider value={{ language, setLanguage, isRTL }}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}
